import {
  AsyncCache,
  CookieNames,
  fromEnv,
  isSdkFlow,
  SDK_FLOW,
  setHeraCookie,
} from "src/helpers";
import axios, { AxiosResponse } from "axios";
import {
  IActivationJSON,
  IActivationKYB,
  IBankValidationPayload,
  IBankValidationResult,
  IKtpScanResult,
  IMerchantJSON,
} from "src/interfaces";
import {
  HERA_BASE_URL,
  LivenessLocale,
  ONEDASHBOARD_BASE_URL,
  SDK_BASE_URL,
} from "src/constants";

axios.defaults.withCredentials = true;

// axios >= 0.28 only sends X-XSRF-TOKEN to the page's own origin, but OneDashboard is cross-origin
// and rejects writes without it ("CSRF validation failed"). Attach it for OneDashboard only.
axios.interceptors.request.use((config) => {
  if (config.url?.startsWith(ONEDASHBOARD_BASE_URL)) {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
    if (match) {
      config.headers = config.headers || {};
      config.headers["X-XSRF-TOKEN"] = decodeURIComponent(match[1]);
    }
  }
  return config;
});

const HERA_LOGIN = `${HERA_BASE_URL}/login`;
const SDK_LOGIN = `${SDK_BASE_URL}/login`;
const SDK_REFRESH = `${SDK_BASE_URL}/refresh`;

let wasJustRefreshed = ((): boolean => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (!params.has("_refreshed")) return false;
  const cleaned = new URL(window.location.href);
  cleaned.searchParams.delete("_refreshed");
  window.history.replaceState(
    window.history.state,
    "",
    cleaned.pathname + cleaned.search + cleaned.hash
  );
  return true;
})();

const redirectToLogin = () => {
  if (isSdkFlow()) {
    window.location.href = SDK_LOGIN;
  } else {
    setHeraCookie(CookieNames.CurrentPath, fromEnv("REACT_APP_HOST"));
    window.location.href = HERA_LOGIN;
  }
};

const redirectToSdkRefresh = () => {
  const callbackUrl = window.location.href;
  window.location.href = `${SDK_REFRESH}?callbackUrl=${encodeURIComponent(
    callbackUrl
  )}`;
};

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    if (wasJustRefreshed) {
      wasJustRefreshed = false;
    }
    return response;
  },
  (error: any) => {
    return new Promise((resolve, reject) => {
      const originalRequest = error.config;
      const requiresTokenRefresh =
        error?.response?.status === 401 && !error?.config?.__isRetryRequest;

      if (!requiresTokenRefresh) {
        reject(error);
        return;
      }

      if (isSdkFlow()) {
        if (wasJustRefreshed) {
          redirectToLogin();
        } else {
          redirectToSdkRefresh();
        }
        return;
      }

      originalRequest.__isRetryRequest = true;
      const heraUrl = `${HERA_BASE_URL}/api/v1/auth/refresh-token`;

      const refreshed = axios
        .get(heraUrl, { withCredentials: true })
        .then((refreshResponse: AxiosResponse) => {
          const accessToken = refreshResponse.data?.data?.access_token;
          if (!accessToken) {
            redirectToLogin();
            return;
          }
          PassportService.globalToken = accessToken;
          originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
          return axios(originalRequest);
        })
        .catch(() => {
          redirectToLogin();
        });

      resolve(refreshed);
    });
  }
);

export class PassportService extends AsyncCache {
  static globalToken = "";
  token = "";

  set tokenValue(value: string) {
    this.token = value;
    PassportService.globalToken = value;
  }

  get currentToken() {
    return this.token || PassportService.globalToken;
  }

  getCurrentUser() {
    return axios.get(`${ONEDASHBOARD_BASE_URL}/api/passport/users/me`, {
      headers: {
        Authorization: "Bearer " + this.currentToken,
      },
    });
  }

  getUserOutlet() {
    return axios.get(`${ONEDASHBOARD_BASE_URL}/api/passport/merchant`, {
      headers: {
        Authorization: "Bearer " + this.currentToken,
      },
    });
  }

  updateOutlet(data: Partial<IMerchantJSON>) {
    return axios.patch(`${ONEDASHBOARD_BASE_URL}/api/passport/merchant`, data, {
      headers: {
        Authorization: "Bearer " + this.currentToken,
      },
    });
  }

  addFeatureTypes(features: string[]) {
    return axios.patch(
      `${ONEDASHBOARD_BASE_URL}/api/merchants/settings/feature`,
      { activate_features: features },
      {
        headers: {
          Authorization: "Bearer " + this.currentToken,
        },
      }
    );
  }

  validateBankAccount(data: IBankValidationPayload) {
    return axios.post<IBankValidationResult>(
      `${ONEDASHBOARD_BASE_URL}/api/passport/account-validation`,
      data,
      {
        headers: {
          Authorization: "Bearer " + this.currentToken,
        },
      }
    );
  }

  postBankAccount(data: Partial<IMerchantJSON>) {
    return axios.patch(`${ONEDASHBOARD_BASE_URL}/api/passport/merchant`, data, {
      headers: {
        Authorization: "Bearer " + this.currentToken,
      },
    });
  }

  postConsent(version: string) {
    return axios.post(
      `${ONEDASHBOARD_BASE_URL}/api/merchant/onboarding/consents`,
      { privacy_policy_version: version },
      {
        headers: {
          Authorization: "Bearer " + this.currentToken,
        },
      }
    );
  }

  getPassportActivation() {
    return axios.get(`${ONEDASHBOARD_BASE_URL}/api/passport/onboarding`, {
      headers: {
        Authorization: "Bearer " + this.currentToken,
      },
    });
  }

  updatePassportActivation(data: Partial<IActivationJSON>) {
    return axios.post(
      `${ONEDASHBOARD_BASE_URL}/api/passport/onboarding`,
      data,
      {
        headers: {
          Authorization: "Bearer " + this.currentToken,
        },
      }
    );
  }

  startKtpScan(data: { document_type: string; file_type: string }) {
    return axios.post<{ data: IKtpScanResult }>(
      `${ONEDASHBOARD_BASE_URL}/api/passport/onboarding/ktp-scan`,
      data,
      {
        headers: {
          Authorization: "Bearer " + this.currentToken,
        },
      }
    );
  }

  getKtpScanResult(documentType: string) {
    return axios.get<{ data: IKtpScanResult }>(
      `${ONEDASHBOARD_BASE_URL}/api/passport/onboarding/ktp-scan`,
      {
        params: { document_type: documentType },
        headers: {
          Authorization: "Bearer " + this.currentToken,
        },
      }
    );
  }

  doPassportKYB(data: IActivationKYB) {
    return axios.post(
      `${ONEDASHBOARD_BASE_URL}/api/passport/onboarding/kyb`,
      data,
      {
        headers: {
          Authorization: "Bearer " + this.currentToken,
        },
      }
    );
  }

  generateUrl(userId: number, key: string) {
    return axios.post(
      fromEnv<string>("REACT_APP_MARS_HOST") + `/v1/users/${userId}/upload_url`,
      {
        key,
      },
      {
        headers: {
          Authorization: "Bearer " + this.currentToken,
          "Authentication-Type": "go-id",
        },
      }
    );
  }

  uploadFile(patchUrl: string, file: File) {
    return axios.put(patchUrl, file);
  }

  generateUrlOss(documentType: string, fileType: string) {
    return axios.post(
      `${ONEDASHBOARD_BASE_URL}/api/passport/onboarding/presigned-url`,
      {
        document_type: documentType,
        file_type: fileType,
      },
      {
        headers: {
          Authorization: "Bearer " + this.currentToken,
          "Content-Type": "application/json",
        },
      }
    );
  }

  uploadFileOss(patchUrl: string, file: File) {
    return axios.put(patchUrl, file, {
      headers: {
        "Content-Type": file.type,
      },
    });
  }

  createLivenessLaunchUrl(
    useQR: boolean,
    isMobile: boolean,
    locale: LivenessLocale
  ) {
    return axios.post(
      `${ONEDASHBOARD_BASE_URL}/api/passport/onboarding/liveness/launch-url`,
      {
        force_qr: useQR,
        allow_qr: !useQR,
        portrait_only: isMobile,
        locale: locale,
        flow: isSdkFlow() ? SDK_FLOW : null,
      },
      {
        headers: {
          Authorization: "Bearer " + this.currentToken,
        },
      }
    );
  }

  verifyLiveness(sessionId: string) {
    return axios.post(
      `${ONEDASHBOARD_BASE_URL}/api/passport/onboarding/liveness/verify`,
      {
        session_id: sessionId,
      }
    );
  }
}
